import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { ContentCopy, Clear, QrCode, Close, Download, Email } from '@mui/icons-material';
import QRCode from 'qrcode';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import axiosInstance from '../utils/axiosConfig';

interface Strand {
  _id: string;
  name: string;
  slug: string;
  description: string;
  type: string;
  startDate: string;
  endDate: string;
  status: string;
  published: boolean;
  location: string;
  bandCount: number;
  genres: string[];
  curator: string;
  bands?: Array<{
    _id: string;
    artistName: string;
  }>;
}

const StrandsListingPage: React.FC = () => {
  const [allStrands, setAllStrands] = useState<Strand[]>([]);
  const [filteredStrands, setFilteredStrands] = useState<Strand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('with-bands');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [bandFilters, setBandFilters] = useState<string[]>([]);
  const [copySuccess, setCopySuccess] = useState<string>('');
  const [qrCodes, setQrCodes] = useState<{[key: string]: string}>({});
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedStrand, setSelectedStrand] = useState<Strand | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailData, setEmailData] = useState({
    to: '',
    subject: '',
    message: ''
  });
  const [emailSending, setEmailSending] = useState(false);

  const copyToClipboard = async (slug: string) => {
    try {
      const url = `${window.location.origin}/strands/${slug}`;
      await navigator.clipboard.writeText(url);
      setCopySuccess(slug);
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const addBandFilter = (bandName: string) => {
    if (!bandFilters.includes(bandName)) {
      setBandFilters([...bandFilters, bandName]);
    }
  };

  const removeBandFilter = (bandName: string) => {
    setBandFilters(bandFilters.filter((name: string) => name !== bandName));
  };

  const openQrModal = (strand: Strand) => {
    setSelectedStrand(strand);
    setQrModalOpen(true);
  };

  const closeQrModal = () => {
    setQrModalOpen(false);
    setSelectedStrand(null);
  };

  const openEmailModal = (strand: Strand) => {
    setSelectedStrand(strand);
    setEmailData({
      to: '',
      subject: `Check out this music strand: ${strand.name}`,
      message: `Hi,\n\nI wanted to share this music strand with you: ${strand.name}\n\n${strand.description}\n\nYou can view all the featured bands and details at:\n${window.location.origin}/strands/${strand.slug}\n\nBest regards`
    });
    setEmailModalOpen(true);
  };

  const closeEmailModal = () => {
    setEmailModalOpen(false);
    setSelectedStrand(null);
    setEmailData({ to: '', subject: '', message: '' });
  };

  const sendEmail = async () => {
    if (!selectedStrand || !emailData.to.trim()) return;
    
    setEmailSending(true);
    try {
      await axiosInstance.post('/api/strands/send-email', {
        to: emailData.to,
        subject: emailData.subject,
        message: emailData.message,
        strandId: selectedStrand._id,
        strandUrl: `${window.location.origin}/strands/${selectedStrand.slug}`,
        qrCodeData: qrCodes[selectedStrand._id]
      });
      
      alert('Email sent successfully!');
      closeEmailModal();
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email. Please try again.');
    } finally {
      setEmailSending(false);
    }
  };
  // PDF generation function
  const generatePDF = async (strand: Strand) => {
    const pdf = new jsPDF();
    
    // Title
    pdf.setFontSize(20);
    pdf.text(strand.name, 20, 30);
    
    // Description
    pdf.setFontSize(12);
    const descriptionLines = pdf.splitTextToSize(strand.description, 170);
    pdf.text(descriptionLines, 20, 50);
    
    // QR Code
    if (qrCodes[strand._id]) {
      pdf.addImage(qrCodes[strand._id], 'PNG', 150, 20, 40, 40);
    }
    
    // URL
    pdf.setFontSize(10);
    pdf.text(`${window.location.origin}/strands/${strand.slug}`, 20, 70);
    
    // Dates
    const startDate = new Date(strand.startDate).toLocaleDateString();
    const endDate = new Date(strand.endDate).toLocaleDateString();
    pdf.text(`${startDate} - ${endDate}`, 20, 80);
    
    // Band list with details
    if (strand.bands && strand.bands.length > 0) {
      pdf.setFontSize(14);
      pdf.text('Featured Bands:', 20, 100);
      
      let yPosition = 110;
      for (const band of strand.bands) {
        // Fetch full band details for each band
        try {
          const bandResponse = await axiosInstance.get(`/api/bandrecommendations/${band._id}`);
          const bandData = bandResponse.data;
          
          pdf.setFontSize(12);
          pdf.text(bandData.artistName, 20, yPosition);
          
          // Genres
          if (bandData.genres && bandData.genres.length > 0) {
            pdf.setFontSize(10);
            pdf.text(`Genres: ${bandData.genres.join(', ')}`, 25, yPosition + 7);
          }
          
          // Band makeup
          pdf.text(`${bandData.totalMembers || 0} members: ${bandData.genderRatio || 'N/A'}`, 25, yPosition + 14);
          
          // Instrumentation
          if (bandData.instrumentation && bandData.instrumentation.length > 0) {
            const instruments = bandData.instrumentation.map((inst: any) => 
              inst.performer?.name ? `${inst.instrumentName} - ${inst.performer.name}` : inst.instrumentName
            ).join(', ');
            const instrumentLines = pdf.splitTextToSize(`Instrumentation: ${instruments}`, 170);
            pdf.text(instrumentLines, 25, yPosition + 21);
            yPosition += instrumentLines.length * 7;
          }
          
          yPosition += 35;
          
          // Add new page if running out of space
          if (yPosition > 250) {
            pdf.addPage();
            yPosition = 30;
          }
          
        } catch (error) {
          console.error('Error fetching band details for PDF:', error);
          // Fallback to basic info
          pdf.setFontSize(12);
          pdf.text(band.artistName, 20, yPosition);
          yPosition += 20;
        }
      }
    }
    
    // Download
    pdf.save(`${strand.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_lineup.pdf`);
  };

  const clearAllFilters = () => {
    setStatusFilter('with-bands');
    setTypeFilter('all');
    setBandFilters([]);
  };

  useEffect(() => {
    const fetchStrandsWithCounts = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get('/api/strands/with-counts');
        setAllStrands(response.data);
        
        // Generate QR codes for all strands (large size for modal)
        const qrPromises = response.data.map(async (strand: Strand) => {
          const url = `${window.location.origin}/strands/${strand.slug}`;
          const qrCodeDataUrl = await QRCode.toDataURL(url, { width: 300, margin: 2 });
          return { [strand._id]: qrCodeDataUrl };
        });
        
        const qrResults = await Promise.all(qrPromises);
        const qrCodesMap = qrResults.reduce((acc, curr) => ({ ...acc, ...curr }), {});
        setQrCodes(qrCodesMap);
        
        setError(null);
      } catch (error) {
        console.error('Error fetching strands:', error);
        setError('Failed to load strands');
      } finally {
        setLoading(false);
      }
    };

    fetchStrandsWithCounts();
  }, []);

  useEffect(() => {
    let filtered = [...allStrands];

    // Apply status filter
    if (statusFilter === 'with-bands') {
      filtered = filtered.filter(strand => strand.bandCount > 0);
    } else if (statusFilter === 'without-bands') {
      filtered = filtered.filter(strand => strand.bandCount === 0);
    } else if (statusFilter === 'published') {
      filtered = filtered.filter(strand => strand.published);
    } else if (statusFilter === 'unpublished') {
      filtered = filtered.filter(strand => !strand.published);
    }

    // Apply type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(strand => strand.type === typeFilter);
    }

    // Apply band filters
    if (bandFilters.length > 0) {
      filtered = filtered.filter(strand => 
        strand.bands?.some(band => bandFilters.includes(band.artistName))
      );
    }

    // Sort: strands with bands first, then by band count descending
    filtered.sort((a, b) => {
      if (a.bandCount > 0 && b.bandCount === 0) return -1;
      if (a.bandCount === 0 && b.bandCount > 0) return 1;
      return b.bandCount - a.bandCount;
    });

    setFilteredStrands(filtered);
  }, [allStrands, statusFilter, typeFilter, bandFilters]);

  const handleStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setStatusFilter(event.target.value);
  };

  const handleTypeFilterChange = (event: SelectChangeEvent<string>) => {
    setTypeFilter(event.target.value);
  };

  // Get unique types for filter dropdown
  const availableTypes = Array.from(new Set(allStrands.map(strand => strand.type))).filter(Boolean);

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h4" component="h1">
            All Promotional Strands ({filteredStrands.length})
          </Typography>
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/strands/new"
          >
            Create New Strand
          </Button>
        </Box>

        {/* Filters */}
        <Box display="flex" gap={2} mb={4} flexWrap="wrap" alignItems="center">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              label="Filter by Status"
            >
              <MenuItem value="with-bands">With Bands ({allStrands.filter(s => s.bandCount > 0).length})</MenuItem>
              <MenuItem value="without-bands">Without Bands ({allStrands.filter(s => s.bandCount === 0).length})</MenuItem>
              <MenuItem value="all">All Strands ({allStrands.length})</MenuItem>
              <MenuItem value="published">Published ({allStrands.filter(s => s.published).length})</MenuItem>
              <MenuItem value="unpublished">Unpublished ({allStrands.filter(s => !s.published).length})</MenuItem>
            </Select>
          </FormControl>

          {availableTypes.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={handleTypeFilterChange}
                label="Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                {availableTypes.map(type => (
                  <MenuItem key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Band Filters */}
          {bandFilters.length > 0 && (
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
              <Typography variant="body2" color="text.secondary">
                Filtering by bands:
              </Typography>
              {bandFilters.map((bandName: string) => (
                <Chip
                  key={bandName}
                  label={bandName}
                  onDelete={() => removeBandFilter(bandName)}
                  size="small"
                  color="primary"
                />
              ))}
            </Box>
          )}

          {(bandFilters.length > 0 || statusFilter !== 'with-bands' || typeFilter !== 'all') && (
            <Button
              variant="outlined"
              size="small"
              onClick={clearAllFilters}
              startIcon={<Clear />}
            >
              Clear All Filters
            </Button>
          )}
        </Box>

        <Grid container spacing={3}>
          {filteredStrands.map((strand) => (
            <Grid item xs={12} md={6} lg={4} key={strand._id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box flexGrow={1}>
                      <Typography variant="h5" component="h2">
                        {strand.name}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          /strands/{strand.slug}
                        </Typography>
                        <Tooltip title={copySuccess === strand.slug ? "Copied!" : "Copy strand URL"}>
                          <IconButton
                            size="large"
                            onClick={() => copyToClipboard(strand.slug)}
                            sx={{ padding: '2px' }}
                          >
                            <ContentCopy sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      {/* QR Code Icon */}
                      <Tooltip title="Show QR Code">
                        <IconButton
                          size="small"
                          onClick={() => openQrModal(strand)}
                          sx={{ padding: '4px' }}
                        >
                          <QrCode sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                      {/* Email Icon */}
                      <Tooltip title="Email Strand">
                        <IconButton
                          size="small"
                          onClick={() => openEmailModal(strand)}
                          sx={{ padding: '4px' }}
                        >
                          <Email sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                      <Chip
                        label={`${strand.bandCount} bands`}
                        color={strand.bandCount > 0 ? "primary" : "default"}
                        size="small"
                      />
                    </Box>
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {strand.description}
                  </Typography>

                  {strand.location && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      📍 {strand.location}
                    </Typography>
                  )}

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    📅 {new Date(strand.startDate).toLocaleDateString()} - {new Date(strand.endDate).toLocaleDateString()}
                  </Typography>

                  {strand.genres && strand.genres.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      {strand.genres.map((genre) => (
                        <Chip
                          key={genre}
                          label={genre}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  )}

                  {strand.curator && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Curated by: {strand.curator}
                    </Typography>
                  )}

                  {/* Band Names */}
                  {strand.bands && strand.bands.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                        Featured Bands:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {strand.bands.map((band) => (
                          <Chip
                            key={band._id}
                            label={band.artistName}
                            size="small"
                            color="primary"
                            variant="filled"
                            onClick={() => addBandFilter(band.artistName)}
                            sx={{ cursor: 'pointer' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  <Box display="flex" gap={1} mt="auto">
                    <Button
                      variant="contained"
                      size="small"
                      component={RouterLink}
                      to={`/strands/${strand.slug}`}
                      fullWidth
                      disabled={strand.bandCount === 0}
                      sx={{ mb: 1 }}
                    >
                      View Live Strand
                    </Button>
                    {strand.bandCount > 0 && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => generatePDF(strand)}
                        fullWidth
                        startIcon={<Download />}
                      >
                        Export PDF
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {filteredStrands.length === 0 && (
          <Box textAlign="center" mt={4}>
            <Typography variant="h6" color="text.secondary">
              No strands found matching current filters
            </Typography>
            <Button
              variant="outlined"
              onClick={clearAllFilters}
              sx={{ mt: 2, mr: 2 }}
            >
              Clear Filters
            </Button>
            <Button
              variant="contained"
              color="primary"
              component={RouterLink}
              to="/strands/new"
              sx={{ mt: 2 }}
            >
              Create New Strand
            </Button>
          </Box>
        )}
      </Box>

      {/* QR Code Modal */}
      <Dialog 
        open={qrModalOpen} 
        onClose={closeQrModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            QR Code for {selectedStrand?.name}
          </Typography>
          <IconButton onClick={closeQrModal} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 3 }}>
          {selectedStrand && qrCodes[selectedStrand._id] && (
            <>
              <Box sx={{ mb: 2 }}>
                <img 
                  src={qrCodes[selectedStrand._id]} 
                  alt={`QR code for ${selectedStrand.name}`}
                  style={{ maxWidth: '100%', height: 'auto' }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Scan this QR code to visit:
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  bgcolor: 'grey.100', 
                  p: 1, 
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  wordBreak: 'break-all'
                }}
              >
                {window.location.origin}/strands/{selectedStrand.slug}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => copyToClipboard(selectedStrand?.slug || '')}
            variant="outlined"
          >
            {copySuccess === selectedStrand?.slug ? 'Copied!' : 'Copy URL'}
          </Button>
          <Button onClick={closeQrModal} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Modal */}
      <Dialog 
        open={emailModalOpen} 
        onClose={closeEmailModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Email {selectedStrand?.name}
          </Typography>
          <IconButton onClick={closeEmailModal} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="To (Email Address)"
              type="email"
              value={emailData.to}
              onChange={(e) => setEmailData(prev => ({ ...prev, to: e.target.value }))}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="Subject"
              value={emailData.subject}
              onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Message"
              multiline
              rows={6}
              value={emailData.message}
              onChange={(e) => setEmailData(prev => ({ ...prev, message: e.target.value }))}
            />
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            The email will include:
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            <li>QR code for easy mobile access</li>
            <li>Direct link to the strand page</li>
            <li>List of featured bands</li>
            <li>Event dates and description</li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEmailModal}>
            Cancel
          </Button>
          <Button 
            onClick={sendEmail}
            variant="contained"
            disabled={!emailData.to.trim() || emailSending}
            startIcon={<Email />}
          >
            {emailSending ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default StrandsListingPage;